import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useDispatch, useSelector } from '_common/hooks';
import { navigateToEditor, navigateToObject } from 'router/navigation';

import { setObjectPreview } from '_common/components/ObjectPreview/ObjectPreviewSlice';
import { useGetUserSettingsQuery } from '_common/services/api/UserApi';
import { useGetMetadataListQuery } from 'App/redux/MetadataApi';

import StorageEmptyState from '../components/StorageEmptyState/StorageEmptyState';

import { UserCard } from '_common/components';

import {
  ThunksTable,
  TableColumn,
  onDoubleClickHandler,
  rowHasErrorCallback,
  objectIsOverdue,
  Cell,
  Footer,
  SelectionCounter,
} from '_common/components/Table';

import StoragePageCell from './Cell';

type StorageTableRow = Objekt;

type StoragePageTableProps = {
  fetchObjects: (listParams: Request.AllListParams) => Promise<Request.FetchResponse>;
  filters: Request.FilterParams;
  current: Objekt;
};

const PAGE_IDENTITY = 'storage';

const StoragePageTable = ({ fetchObjects, filters, current }: StoragePageTableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const data = useSelector((state) => state.app.data);
  const list = useSelector((state) => state.table.identity.storage.list);
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: metadata } = useGetMetadataListQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: result.data ?? {} }),
  });

  const veevaDocNumberId = Object.keys(metadata).find(
    (key) => metadata?.[key]?.trigger === 'veeva_doc_number',
  );
  const veevaGlobalId = Object.keys(metadata).find(
    (key) => metadata?.[key]?.trigger === 'veeva_global_id',
  );

  const rowsData = useMemo<StorageTableRow[]>(() => {
    const rowsData = list?.map<StorageTableRow>((objectId) => {
      const object = data[objectId];
      return {
        ...object,
      };
    });
    return rowsData;
  }, [list, data]);

  const columns = useMemo<TableColumn<StorageTableRow, keyof TableCustomisation>[]>(() => {
    const columns: TableColumn<StorageTableRow, keyof TableCustomisation>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        minWidth: 240,
        header: intl.formatMessage({ id: 'storage.browserHeader.name' }),
        cell: (info) => {
          const object = info.row.original;

          return (
            <StoragePageCell object={object} testId={`name-column`}>
              <Cell.ObjectName object={object} testId={`storage-${object.id}-object-name`} />
            </StoragePageCell>
          );
        },
      },
      {
        id: 'due_date',
        accessorKey: 'due_date',
        header: undefined,
        enableSorting: false,
        size: 40,
        cell: (info) => {
          const object = info.row.original;
          return (
            <StoragePageCell testId={`due-date-column`} object={object}>
              <Cell.ObjectDueDate object={object} testId={`storage-${object.id}-object-due-date`} />
            </StoragePageCell>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        header: intl.formatMessage({ id: 'storage.browserHeader.status' }),
        size: 160,
        cell: (info) => {
          const object = info.row.original;

          return (
            <StoragePageCell testId={`status-column`} object={object}>
              <Cell.ObjectStatus object={object} testId="object-status" />
            </StoragePageCell>
          );
        },
      },

      {
        id: 'time.modification',
        accessorKey: 'time.modification',
        header: intl.formatMessage({ id: 'storage.browserHeader.dateModified' }),
        size: 160,
        cell: (info) => {
          const object = info.row.original;

          return (
            <StoragePageCell testId={`date-modified-column`} object={object}>
              <Cell.DateTime
                date={{ date: object.time.modification }}
                time={{ time: object.time.modification }}
              />
            </StoragePageCell>
          );
        },
      },

      {
        id: 'shared_with',
        accessorKey: 'shared_with',
        header: intl.formatMessage({ id: 'storage.browserHeader.sharedWith' }),
        size: 160,
        cell: (info) => {
          const object = info.row.original;

          return (
            <StoragePageCell testId={`shared-column`} object={object}>
              <Cell.Collaborators object={object} />
            </StoragePageCell>
          );
        },
      },
      {
        id: 'owner',
        accessorKey: 'owner',
        enableSorting: false,
        header: intl.formatMessage({ id: 'global.owner' }),
        size: 200,
        cell: (info) => {
          const object = info.row.original;

          return (
            <Cell testId={`${object.id}-owner-column`} disabled={object.status === 'processing'}>
              <UserCard userId={object.owner} />
            </Cell>
          );
        },
      },
      {
        id: 'time.creation',
        accessorKey: 'time.creation',
        header: intl.formatMessage({ id: 'CREATION_DATE' }),
        size: 160,
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell
              testId={`${object.id}-creation-date-column`}
              disabled={object.status === 'processing'}
            >
              <Cell.DateTime
                date={{ date: object.time.creation }}
                time={{ time: object.time.creation }}
              />
            </Cell>
          );
        },
      },
    ];

    if (veevaDocNumberId) {
      columns.push({
        id: 'metadata.veeva_doc_number',
        accessorFn: (object) => (veevaDocNumberId ? object.metadata?.[veevaDocNumberId] : null),
        size: 160,
        header: intl.formatMessage({ id: 'VEEVA_DOC_NUMBER' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell
              testId={`${object.id}-veeva-doc-number`}
              disabled={object.status === 'processing'}
            >
              {veevaDocNumberId ? object?.metadata?.[veevaDocNumberId] : null}
            </Cell>
          );
        },
      });
    }

    if (veevaGlobalId) {
      columns.push({
        id: 'metadata.veeva_global_id',
        accessorFn: (object) => (veevaGlobalId ? object?.metadata?.[veevaGlobalId] : null),
        size: 160,
        header: intl.formatMessage({ id: 'VEEVA_GLOBAL_ID' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-veeva-global-id`} disabled={object.status === 'processing'}>
              {veevaGlobalId ? object?.metadata?.[veevaGlobalId] : null}
            </Cell>
          );
        },
      });
    }
    return columns.filter((column) => userSettings?.tables.explorer[column.id]);
  }, [userSettings]);

  //@ts-expect-error
  const handleRowDoubleClick: onDoubleClickHandler<StorageTableRow> = (row, originalEvent) => {
    const currentObject = data[row.id];

    if (currentObject.status === 'processing' || currentObject.status === 'broken') {
      return;
    }

    let type = currentObject.type;
    let id = currentObject.id;
    switch (type) {
      case 'document':
        navigateToEditor(id);
        break;
      case 'file':
        dispatch(setObjectPreview({ id: currentObject.id }));
        originalEvent.preventDefault();
        break;
      case 'folder':
        navigateToObject('folder', id);
        break;
      case 'dopdf':
        window.open(`/pdf/${id}`, '_blank');
        break;
      case 'presentation':
        window.open(`/presentation/${id}`, '_blank');
        break;
      default:
        break;
    }
  };

  const rowHasError: rowHasErrorCallback<StorageTableRow> = (row) => {
    const object = data[row.id];
    return objectIsOverdue(object);
  };

  const renderEmptyState = () => <StorageEmptyState current={current} filters={filters} />;

  return (
    <ThunksTable
      identity={PAGE_IDENTITY}
      options={{ columns, data: rowsData }}
      lazyLoading
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      renderFooter={() => (
        <Footer>
          <SelectionCounter identity={PAGE_IDENTITY} />
        </Footer>
      )}
      renderEmptyState={renderEmptyState}
      fetchObjects={fetchObjects}
      onDoubleClick={handleRowDoubleClick}
      rowHasError={rowHasError}
      testId="storage-page"
    />
  );
};

export default StoragePageTable;
